import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useToast from '@hooks/useToast';

import { Stack, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';

import MainCard from '@components/ui/cards/MainCard.tsx';
import MainTable from '@components/MainTable';
import MainTablePagination from '@components/MainTable/MainTablePagination';
import { useTanStackTable } from '@hooks/useTanStackTable';
import { routes } from '@shared/constants/paths.ts';
import { DocumentTemplate } from '@app/types/document-template';
import { documentTemplateTableColumns } from '@features/admin-document-template/containers/DocumentTemplateTable/columns.tsx';
import { deleteDocumentTemplate } from '@api/document-template';
import useDataFetcher from '@hooks/useDataFetcher/useDataFetcher.ts';
import { urls } from '@api/urls.ts';
import DeleteModal from '@components/DeleteModal';
import InputFieldWithDebounce from '@components/InputFieldWithDebounce/ui/InputFieldWithDebounce.tsx';

function DocumentTemplateTable() {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<DocumentTemplate | null>(null);
  const { showSuccess } = useToast();
  const { data, mutate, isLoading } = useDataFetcher<DocumentTemplate[]>({
    url: urls.documentTemplate.base,
  });

  const createDocument = () => {
    navigate(routes.superAdmin.documentTemplate.create);
  };

  const { table, setData } = useTanStackTable<DocumentTemplate>({
    tableData: [],
    columnConfig: documentTemplateTableColumns,
    options: {
      meta: {
        handleDeleteRow: async (template: DocumentTemplate) => {
          setSelectedTemplate(template);
          setShowDeleteModal(true);
        },
        handleEditRow: (preview: DocumentTemplate) => {
          navigate(routes.superAdmin.documentTemplate.edit(preview.id));
        },
      },
    },
  });

  const handleDelete = async () => {
    if (!selectedTemplate) return;
    await deleteDocumentTemplate(selectedTemplate.id);
    setShowDeleteModal(false);
    setSelectedTemplate(null);
    showSuccess('Success', 'Your template has been deleted successfully');
    await mutate();
  };

  const onSearchChange = (value: string) => {
    table.setGlobalFilter(value);
  };

  useEffect(() => {
    if (!data) return;
    setData(data);
  }, [data]);

  return (
    <MainCard>
      <Stack gap={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h3">List of Templates</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Box height="100%" display="flex" width="300px">
              <InputFieldWithDebounce
                debounceAction={onSearchChange}
                placeholder="Search template..."
                name="searchInput"
              />
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={createDocument}
              sx={{
                flexGrow: { xs: 1, sm: 0 },
              }}
            >
              Create Template
            </Button>
          </Stack>
        </Stack>
        <Box>
          <MainTable table={table} isLoading={isLoading} />
          <MainTablePagination table={table} isLoading={isLoading} />
        </Box>
      </Stack>

      <DeleteModal
        onCancel={() => {
          setShowDeleteModal(false);
          setSelectedTemplate(null);
        }}
        onConfirm={() => handleDelete()}
        showModal={showDeleteModal}
        title="Delete Template"
        description="Are you sure you want to delete this template?"
      />
    </MainCard>
  );
}

export default DocumentTemplateTable;
